import { Immutable } from "immer";
import React, { useEffect, useRef } from "react";
import { t } from "ttag";

import type { FeaturedOffersCardBlock as Value } from "@reactivated";

import { RichTextBlock } from "@thelabnyc/thelabui/src/components/RichText";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { pushPrebuiltGA4Event } from "../../utils/analytics";
import { isElementVisible } from "../../utils/visibility";
import { getGlobalSpacingClasses } from "../_spacing/globalSpacing";

type IProps = Immutable<{
    value: Value;
}>;

type OfferStructCard = NonNullable<Value["offer_cards"]>[number];
type OfferSnippetCard = NonNullable<Value["cards"]>[number];
type SelectEvent = Immutable<
    Value["select_promotion_events_offer_cards"][number]
>;

/**
 * Faithful client port of the legacy server-rendered
 * ``tsicommon_cms/blocks/featured-offers-card-block.html`` (#33285/#33427).
 *
 * The block renders TWO card sources into ONE ``.offer-card__list``: the
 * ``offer_cards`` ListBlock (struct cards) followed by the ``cards`` ListBlock
 * of ``FeaturedOffersCard`` snippets (which now serialize their full payload
 * -- see the snippet's ``get_data_for_client_render`` -- in the same shape as
 * the struct cards, so both render through one ``OfferCard`` code path).
 *
 * Header parity note: the legacy template did NOT delegate to the shared
 * ``aligned-rich-text-block.html``; it hand-built an
 * ``.aligned-rich-text-block`` section from ``header_content`` fields and --
 * unusually -- rendered the BLOCK-level ``background_style.background_image``
 * inside a ``<picture>`` within that header section. Reproduced exactly.
 * The legacy ``{% if value.header_content %}`` gate tests a StructValue
 * (always truthy), so the header section renders whenever the serialized
 * struct exists.
 *
 * GA4: every event arrives PRE-BUILT from the server (the same
 * ``_build_promotion_event``/``_build_select_promotion_event`` builders the
 * legacy ``{% track_view_promotions %}`` / ``{% select_promotion_tracking_attrs %}``
 * tags used) and is pushed verbatim:
 *
 *   - ``value.view_promotion_events`` fire once on mount, in the legacy push
 *     order (all ``offer_cards``, then all ``cards``; each list 1-indexed
 *     independently), gated on element visibility like the legacy
 *     ``push_to_datalayer.html`` ``_isVisible`` walk.
 *   - ``select_promotion_events_offer_cards`` / ``..._cards`` are
 *     index-aligned click events. Unlike the legacy markup (which fired them
 *     on the "See details" anchor), they now fire on the card's "Shop Now"
 *     CTA, matching the sibling ``FeaturedProductsCardBlock`` (whose
 *     ``select_item`` fires on its "Shop Now" anchor). That CTA is an
 *     ``a.button`` inside the serialized rich-text ``content``
 *     (dangerouslySetInnerHTML), so the click is delegated from the card group
 *     rather than bound to a React element (``null`` event -- no-op -- for
 *     cards without a ``linked_offer``).
 *
 *   Server-side DNT requests serialize no events (legacy tag parity).
 *
 * Like other shared block ports, images render as plain eager ``<img>`` tags
 * from the serialized ``height-250`` rendition (the legacy
 * ``{% wagtail_image ... specs='height-250' %}`` rendered exactly that under
 * the puppeteer UA; the lazysizes placeholder markup is not reproduced --
 * AwardColumns/FeaturedProductLines precedent).
 */

/** Serialized-image subset shared by struct cards and the snippet payload. */
type CardImage = NonNullable<OfferStructCard["product_image"]>;

const OfferCardImage = (
    props: Immutable<{ image: CardImage | null | undefined }>,
): React.JSX.Element | null => {
    const image = props.image;
    if (!image) {
        return null;
    }
    return (
        <img
            src={image.renditions["height-250"]?.url ?? image.url}
            alt={image.title}
            loading="lazy"
            decoding="async"
        />
    );
};

const OfferCard = (
    props: Immutable<{
        groupLabel: string;
        linkLabel: string;
        image: CardImage | null | undefined;
        content: string | null | undefined;
        linkedOffer: { url: string; title: string } | null | undefined;
        selectEvent: SelectEvent;
    }>,
): React.JSX.Element => {
    // The "Shop Now" CTA lives in serialized rich text (dangerouslySetInnerHTML),
    // so delegate from the group and match `a.button` on the way up (this
    // excludes the bare "See details" anchor by selector). Any `a.button` the
    // editor styles in the card fires select_promotion, so the promo CTA must
    // carry that class.
    const handleSelectPromotion = (e: React.MouseEvent<HTMLDivElement>) => {
        // `Element` (not `HTMLElement`): `closest` lives on `Element`, so this
        // also matches an SVG icon nested inside the CTA and no-ops on any
        // non-element target.
        const target = e.target;
        if (target instanceof Element && target.closest("a.button")) {
            pushPrebuiltGA4Event(props.selectEvent);
        }
    };
    return (
        <li className="offer-card">
            <div
                className="offer-card__group"
                role="group"
                aria-label={props.groupLabel}
                onClick={handleSelectPromotion}
            >
                <OfferCardImage image={props.image} />

                {props.content != null && (
                    <RichTextBlock
                        value={props.content}
                        className="offer-card__content"
                    />
                )}

                {props.linkedOffer && (
                    <a
                        href={props.linkedOffer.url}
                        aria-label={props.linkLabel}
                    >
                        {t`See details`}
                    </a>
                )}
            </div>
        </li>
    );
};

export const FeaturedOffersCardBlock = (props: IProps): React.JSX.Element => {
    const v = props.value;
    const offerCards: readonly OfferStructCard[] = v.offer_cards ?? [];
    const snippetCards: readonly OfferSnippetCard[] = v.cards ?? [];
    const offerCardsLen = offerCards.length;
    const selectEventsOfferCards = v.select_promotion_events_offer_cards;
    const selectEventsCards = v.select_promotion_events_cards;

    const sectionRef = useRef<HTMLElement | null>(null);
    const firedRef = useRef(false);

    // Fire the ``view_promotion`` impressions exactly once on mount from the
    // PRE-BUILT server events, in the legacy push order. Visibility gate
    // mirrors the legacy ``push_to_datalayer.html`` ``_isVisible`` walk.
    useEffect(() => {
        if (firedRef.current) {
            return;
        }
        firedRef.current = true;
        if (!isElementVisible(sectionRef.current)) {
            return;
        }
        for (const event of v.view_promotion_events) {
            pushPrebuiltGA4Event(event);
        }
        // Fire-on-mount only: the block value is static per page render.
    }, []);

    const cardList = (
        <ul className="offer-card__list" aria-label={t`Featured offers`}>
            {offerCards.map((card, i) => {
                const num = i + 1;
                const name = card.linked_offer?.title;
                return (
                    <OfferCard
                        key={`offer-card-${i}`}
                        groupLabel={t`Featured offer ${num}`}
                        linkLabel={
                            name
                                ? t`See details for ${name}`
                                : t`See details for featured offer ${num}`
                        }
                        image={card.product_image}
                        content={card.content}
                        linkedOffer={card.linked_offer}
                        selectEvent={selectEventsOfferCards[i]}
                    />
                );
            })}
            {snippetCards.map((card, i) => {
                // The legacy snippet loop offsets its fallback numbering by
                // the struct-card count (`forloop.counter|add:offer_cards_len`).
                const num = i + 1 + offerCardsLen;
                const cardName = card?.name;
                const linkedTitle = card?.linked_offer?.title;
                const name = cardName || linkedTitle;
                return (
                    <OfferCard
                        key={`card-${i}`}
                        groupLabel={
                            cardName ? cardName : t`Featured offer ${num}`
                        }
                        linkLabel={
                            name
                                ? t`See details for ${name}`
                                : t`See details for featured offer ${num}`
                        }
                        image={card?.product_image}
                        content={card?.content}
                        linkedOffer={card?.linked_offer}
                        selectEvent={selectEventsCards[i]}
                    />
                );
            })}
        </ul>
    );

    const headerBgImage = v.background_style?.background_image;

    return (
        <section
            ref={sectionRef}
            className={concatClassNames([
                "featured-offers-card-block",
                v.background_style?.style ?? "",
                getGlobalSpacingClasses(v.spacing),
                v.theme ?? "",
            ])}
        >
            {v.header_content != null && (
                <section
                    className={concatClassNames([
                        "aligned-rich-text-block",
                        v.header_content.alignment ?? "",
                        v.header_content.background_style?.style ?? "",
                        v.header_content.full_width
                            ? "aligned-rich-text-block--full-width"
                            : "",
                    ])}
                >
                    {v.header_content.content != null && (
                        <RichTextBlock value={v.header_content.content} />
                    )}

                    {headerBgImage && (
                        <picture>
                            <img
                                src={
                                    headerBgImage.renditions["width-2000"]
                                        ?.url ?? headerBgImage.url
                                }
                                alt={headerBgImage.title}
                                loading="lazy"
                                decoding="async"
                            />
                        </picture>
                    )}
                </section>
            )}
            {v.theme === "split" ? (
                <div className="offer-card__container">{cardList}</div>
            ) : (
                cardList
            )}
        </section>
    );
};
